import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import {
  convertDropdownList,
  vehicleCategoryList,
  vehicleEnvironmentalLabelList,
  vehicleFuelTypeList,
  vehicleSizeTypeList,
  vehicleStatusList,
  vehicleTransmissionList,
  vehicleUsedTypeList,
} from 'helpers/utils';
import { IDefaultOption } from 'components/common/select';
import { IOrganizationAssociateParking } from './administratorRoleSlice';
import { IUploadImageRequestParam } from './organizationSlice';
import axios from 'axios';
import { IClientUser } from './userSlice';
import { IAddress } from 'helpers/types';
import { ICompany } from './companySlice';
import { getLanguage } from 'helpers/i18n';
import { getItemFromLocalStorage } from 'helpers/localstorage';
import toast from 'react-hot-toast';
import {
  IAddEditVehicleRequestBody,
  IVehicleAddEditData,
  IVehicleDetails as VehicleDetailsType,
  IVehicleListItem,
  IVehiclesFilters,
  IVehicleAlertItem,
} from 'interfaces/vehicle-interface';
interface IExtendedVehicleDetails {
  _id: string;
  vehicle_id: string;
  vehicle_type: string;
  km: string;
  version: string;
  engine_type: string;
  fuel_type: string;
  parking_id: string;
  environmental_label: false;
  transmission: string;
  is_used: boolean;
  doors: string;
  places: string;
  tire_size: string;
  tuition: string;
  frame: string;
  category: string;
  created_at: string;
  updated_at: string;
}
export interface IVehicle {
  _id: string;
  brand_name: string;
  model: string;
  organization_name: string;
  organization_id?: string;
  purchase_date: string;
  registration_date: string;
  status: string;
  vehicle_image_url: string;
  assigned_user_name?: string;
  plate?: string;
  year: string;
  company_name?: string;
  chasis: string;
  extendedVehicleDetails?: IExtendedVehicleDetails[] | IExtendedVehicleDetails;
  assigned_user_Id?: string;
}

export interface IVehicleDetails extends Omit<IVehicle, 'organization_name'> {
  img_data: { _id: string; url: string; type: string; is_deleted: boolean } | null;
  is_deleted?: boolean;
  organization: IOrganizationAssociateParking | null;
  user_id: IDefaultOption | null;
  user?: IClientUser | null;
  parking?: IAddress;
  vehicle_img: string;
  associatedCompany?: ICompany;
  associated_company: string;
  plate: string;
  chasis: string;
  vehicle_data?: {
    _id: string;
    category: string;
    doors: string;
    engine_type: string;
    environmental_label: boolean;
    frame: string;
    fuel_type: string;
    is_used: string;
    km: string;
    places: string;
    tire_size: string;
    transmission: string;
    tuition: string;
    vehicle_id: string;
    vehicle_type: string;
    version: string;
  } | null;
}
export interface IAddEditVehicleRequestParams {
  vehicle_id?: string;
  user_id?: string;
  vehicle_img: string;
  brand_name: string;
  model: string;
  year: string;
  registration_date: string;
  purchase_date: string;
  status: string;
  vehicle_type: string;
  plate: string;
  chasis: string;
  km: string;
  version: string;
  engine_type: string;
  fuel_type: string;
  parking_id: string;
  organization_id: string;
  environmental_label: boolean;
  transmission: string;
  is_used: boolean;
  associated_company?: string;
  doors?: string;
  places?: string;
  tire_size: string;
  tuition: string;
  frame: string;
  category: string;
}
export interface IStaticDropdownOption {
  id: string;
  name: string;
}
interface IVehiclesState {
  vehicles: IVehicleListItem[];
  vehicleDetails: VehicleDetailsType | null;
  vehicleSizeTypeList: IDefaultOption[];
  vehicleTransmissionList: IDefaultOption[];
  vehicleUsedTypeList: IDefaultOption[];
  vehicleCategoryList: IDefaultOption[];
  vehicleStatusList: IDefaultOption[];
  organizationAssociatedUsers: IDefaultOption[];
  filledVehicleData: IVehicleAddEditData | null;
  vehicleFuelTypeList: IDefaultOption[];
  vehicleEnvLableList: IDefaultOption[];
  selectedVehicleALerts: IVehicleAlertItem[];
  totalPageCount: number;
  selectedVehicleDtcALerts: IVehicleAlertItem[];
  totalCount: number;
}

interface IVehicleResponse {
  vehicleDetails: IVehicleListItem[];
  record_count: number;
}

interface IVehicleAlertsResponse {
  vehicleAlerts: IVehicleAlertItem[];
  record_count: number;
}

const initialState: IVehiclesState = {
  vehicles: [],
  vehicleDetails: null,
  vehicleSizeTypeList: vehicleSizeTypeList,
  vehicleTransmissionList: vehicleTransmissionList,
  vehicleUsedTypeList: vehicleUsedTypeList,
  vehicleCategoryList: vehicleCategoryList,
  vehicleStatusList: vehicleStatusList,
  organizationAssociatedUsers: [],
  filledVehicleData: null,
  vehicleFuelTypeList: vehicleFuelTypeList,
  vehicleEnvLableList: vehicleEnvironmentalLabelList,
  selectedVehicleALerts: [],
  totalPageCount: 0,
  selectedVehicleDtcALerts: [],
  totalCount: 0,
};
const vehicleSlice = createSlice({
  name: 'Vehicles',
  initialState: initialState,
  reducers: {
    setVehiclesList: (state, { payload }: { payload: IVehicleResponse }) => {
      state.vehicles = [...payload.vehicleDetails];
      state.totalPageCount = payload.record_count;
    },
    setVehicleDetails: (state, { payload }: { payload: VehicleDetailsType }) => {
      state.vehicleDetails = payload;
    },
    setUserlistAssociateWithOrganization: (state, { payload }: { payload: IDefaultOption[] }) => {
      state.organizationAssociatedUsers = payload;
    },
    setFilledVehicleDetails: (state, { payload }: { payload: IVehicleAddEditData | null }) => {
      state.filledVehicleData = payload;
    },
    setSelectedVehicleAlertsList: (state, { payload }: { payload: IVehicleAlertsResponse }) => {
      state.selectedVehicleALerts = [...payload.vehicleAlerts];
      state.totalCount = payload.record_count;
    },
     setSelectedVehicleDtcAlertsList: (state, { payload }: { payload: IVehicleAlertItem[] }) => {
      state.selectedVehicleALerts = payload;
    },
  },
});

export const getVehiclesList =
  (
    payload: {
      request?: Partial<IVehiclesFilters>;
      searchString?: string;
      offset?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { request, searchString, offset, limit } = payload;

      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''
      }`;

      const response = await api.post(`/admin/vehicle/getVehicleList${queryString}`, request, {
        signal,
      });

      const { data: responseData } = response;

      dispatch(setVehiclesList(responseData.data));

      return Promise.resolve(responseData.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getVehicleDetails =
  (payload: { vehicle_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { vehicle_id } = payload;
      const { data: responseData } = await api.get(`/vehicle/${vehicle_id}`);
      dispatch(setVehicleDetails(responseData?.data || null));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getUserListAssociateOrganization =
  (payload: { params: { organization_id: string; company_id?: string } }) =>
  async (dispatch: Dispatch) => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.post(`/admin/getUserNames`, { ...params });
      let updateData: IDefaultOption[] = convertDropdownList(responseData?.data, 'user_id', 'id');
      updateData = convertDropdownList(updateData, 'user_name', 'name');
      dispatch(setUserlistAssociateWithOrganization(updateData));
      return Promise.resolve(updateData);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getVehicleUploadImageLocation =
  (payload: { params: IUploadImageRequestParam }) => async () => {
    try {
      const {
        params: { request, file },
      } = payload;
      const constfile = file;
      const { data: responseData } = await api.get('/admin/assets/get/vehicle/img', {
        params: { ...request },
      });
      await axios.put(`${responseData?.data?.url}`, constfile);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const addVehicleAPI = (payload: { request: IAddEditVehicleRequestBody }) => async () => {
  try {
    const { request } = payload;
    const { data: responseData } = await api.post('/admin/vehicle', request);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const editVehicleAPI =
  (payload: { request: IAddEditVehicleRequestBody; vehicle_id: string }) => async () => {
    try {
      const { request, vehicle_id } = payload;
      const { data: responseData } = await api.post(`/admin/vehicle/${vehicle_id}`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const downloadVehicleTemplate = () => async () => {
  try {
    const { data } = await api.get('/admin/vehicle/template', { responseType: 'blob' });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const importVehicles = (payload: { file: File }) => async () => {
  try {
    const { file } = payload;
    const formData = new FormData();
    formData.append('csvfile', file);
    const org = getItemFromLocalStorage('org');
    await axios
      .post(`${process.env.REACT_APP_BASE_URL}/admin/vehicle/import`, formData, {
        headers: {
          Authorization: org?.token,
          lang: getLanguage(),
          'Content-Type': 'multipart/form-data',
        },
      })
      .catch(err => {
        err?.response?.data?.error?.forEach((e: string) =>
          toast.error(e ? e : 'Something went wrong'),
        );
      });
    return Promise.resolve(true);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getVehicleAlerts =
  (payload: { 
    vehicle_id: string; 
    params?: { status: string };
    offset?: number;
    limit?: number;
  }) => async (dispatch: Dispatch) => {
    try {
      const { vehicle_id, params, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;

      const { data: responseData } = 
        await api.get(`/admin/vehicle-alerts/${vehicle_id}${queryString}`, {
          params,
        });
      dispatch(setSelectedVehicleAlertsList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const updateVehicleKm = (payload: { vehicle_id: string }) => async (dispatch: Dispatch) => {
  try {
    const { vehicle_id } = payload;
    const { data: responseData } = await api.get(`/admin/vehicle/force-update/km/${vehicle_id}`);
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const getVehicleDtcAlerts =
  (payload: { vehicle_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { vehicle_id } = payload;
      const { data: responseData } = await api.get(`/vehicle/${vehicle_id}/dtc-codes`);
      dispatch(setSelectedVehicleDtcAlertsList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const {
  setVehiclesList,
  setVehicleDetails,
  setUserlistAssociateWithOrganization,
  setFilledVehicleDetails,
  setSelectedVehicleAlertsList,
  setSelectedVehicleDtcAlertsList
} = vehicleSlice.actions;
export default vehicleSlice.reducer;
